import { createContext, FC, useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useFlags } from '../hooks';
import { entraLogin, getMe } from '../fetch';
import { getCookie, getEnvironment, userLogout } from '../helpers';
import { AppEnvironment, IUser, UserRoles } from '../models';
import { useAccount, useMsal } from '@azure/msal-react';
import {
  InteractionRequiredAuthError,
  PopupRequest,
  PublicClientApplication
} from '@azure/msal-browser';
import { msalConfig, tokenRequest } from '../authConfig';
import { logError } from '../services';

export interface IUserContext {
  setUser: (user: IUser | undefined) => void;
  user: IUser | undefined;
  isProjectManager: boolean;
  isLoading: boolean;
  isEntraLoading: boolean;
  loadUser: (shouldLoad?: boolean) => void;
  isBillingAdmin: boolean;
  isFinancialAdmin: boolean;
  isGlobalAdmin: boolean;
  isWarrantyAdmin: boolean;
  isAdmin: boolean;
  canEdit: boolean;
  hasWarrantyRole: boolean;
  isFieldAdmin: boolean;
  isVendor: boolean;
  isCloser: boolean;
  isField: boolean;
  isAdditionalUser: boolean;
  canImpersonateInternalUser: boolean;
  canImpersonateExternalUser: boolean;
  canAccessFileManagement: boolean;
  canAccessManageDocumentGroups: boolean;
  isPM: boolean;
  isPMOrSuperintendent: boolean;
  isSupervisor: boolean;
  isForeman: boolean;
}

export const UserContext = createContext<IUserContext>({
  user: undefined,
  isProjectManager: false,
  isLoading: false,
  isEntraLoading: false,
  loadUser: () => {},
  setUser: () => {},
  isBillingAdmin: false,
  isFinancialAdmin: false,
  isGlobalAdmin: false,
  isWarrantyAdmin: false,
  isAdmin: false,
  isFieldAdmin: false,
  isVendor: false,
  isCloser: false,
  canEdit: false,
  hasWarrantyRole: false,
  isField: false,
  isAdditionalUser: false,
  canImpersonateInternalUser: false,
  canImpersonateExternalUser: false,
  canAccessFileManagement: false,
  canAccessManageDocumentGroups: false,
  isPM: false,
  isPMOrSuperintendent: false,
  isSupervisor: false,
  isForeman: false
});

interface IUserContextHandlerProps {}

// for zendesk script type support //
declare global {
  interface Window {
    zE?: any;
  }
}

export const UserContextHandler: FC<IUserContextHandlerProps> = ({ children }): JSX.Element => {
  const [user, setUser] = useState<IUser | undefined>(undefined);
  const [isLoading, setIsLoading] = useState(true);

  const featureFlags = useFlags();
  const location = useLocation();
  const cookie =
    getEnvironment() === AppEnvironment.Production ? 'BuildManager-V2' : 'BuildManager-Dev-V2';

  const { accounts, inProgress } = useMsal();
  const isMSALLoading =
    inProgress === 'login' || inProgress === 'logout' || inProgress === 'handleRedirect';
  const account = useAccount(accounts[0] ?? {});
  const msalInstance = new PublicClientApplication(msalConfig);
  const request: PopupRequest = {
    scopes: tokenRequest.scopes,
    account: account || accounts[0],
    resourceRequestUri: msalConfig.auth.clientId
  };

  const fetchUser = async () => {
    try {
      const hasNotCompletedMFAProcess =
        getCookie(cookie) &&
        (user?.userRoles.includes('MFA_Required') ||
          user?.userRoles.includes('MFA_Setup_Required'));
      // if a user is left over in state, but there is no cookie, trigger a logout in v2 and send to /login page
      if (((user && !getCookie(cookie)) || hasNotCompletedMFAProcess) && !account) {
        userLogout();
        setIsLoading(false);
        setUser(undefined);
        window.location.href = '/login';
      }
      // reload the user if a cookie exists because they came from legacy v1
      if (getCookie(cookie) && !user) {
        return loadUser(true);
      }
      // whether user is set or not, set the context loading to false so the rest of the child components will resolve
      setIsLoading(false);
    } catch (e) {
      console.log(e, 'fetchUser');
    }
  };
  useEffect(() => {
    if (Object.keys(account ?? {}).length === 0) {
      fetchUser();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname, account]);

  useEffect(() => {
    const hideOnRoutes = [
      '/login',
      '/forgot-password',
      '/homeowner-portals',
      '/custom-pages/homeowner-portal',
      '/custom-pages/homeowner-portal-thrive'
    ];

    const hideOnRoles = [UserRoles.BLClient];

    const shouldHideWidget =
      hideOnRoutes.includes(location.pathname) ||
      hideOnRoles.some(role => user?.userRoles.includes(role));

    const initializeZendeskWidget = () => {
      if (shouldHideWidget) {
        window.zE('webWidget', 'hide');
      } else {
        window.zE('webWidget', 'show');
      }
    };

    if (user && featureFlags?.zendeskAndHelpTools) {
      if (!document.getElementById('ze-snippet')) {
        const script = document.createElement('script');
        script.id = 'ze-snippet';
        script.type = 'text/javascript';
        script.async = true;
        script.defer = true;
        script.src = `https://static.zdassets.com/ekr/snippet.js?key=47d9796e-639f-4c51-a5f2-ad4c30342a80`;
        document.body.appendChild(script);

        script.onload = () => {
          // Check if window.zE is defined and retry if not
          const checkAndInitialize = (retryCount = 0) => {
            if (window.zE) {
              initializeZendeskWidget();
            } else if (retryCount < 10) {
              // Retry up to 10 times
              setTimeout(() => checkAndInitialize(retryCount + 1), 500); // Wait 500ms before retrying
            }
          };
          checkAndInitialize();
        };
      } else if (window.zE) {
        // If the script is already present and window.zE is defined, initialize the widget
        initializeZendeskWidget();
      }
    }
  }, [featureFlags, location.pathname, user]);

  const handleJWTCookieSwap = async () => {
    setIsLoading(true);

    let authToken: string = null;

    try {
      try {
        const res = await msalInstance.acquireTokenSilent(request);
        authToken = res.accessToken;
      } catch (error) {
        if (error instanceof InteractionRequiredAuthError) {
          try {
            const res = await msalInstance.acquireTokenPopup(request);
            authToken = res.accessToken;
          } catch (error) {
            logError({ error }, 'msal token popup failed');
            msalInstance.logoutRedirect();
          }
        } else {
          logError({ error }, 'msal auth error occurred');
          msalInstance.logoutRedirect();
        }
      }

      await entraLogin(authToken);
      const user = await getMe();
      setUser(user);
    } catch (e) {
      console.log('e', e);
    }
    setIsLoading(false);
  };
  useEffect(() => {
    if (account && !user && !isLoading) {
      handleJWTCookieSwap();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [account, user, isLoading]);

  const loadUser = async (shouldLoad: boolean) => {
    try {
      if (shouldLoad) {
        setIsLoading(true);
      }
      const res = await getMe();
      const hasNotCompletedMFAProcess =
        getCookie(cookie) &&
        (res?.userRoles.includes('MFA_Required') || res?.userRoles.includes('MFA_Setup_Required'));
      if (hasNotCompletedMFAProcess) {
        userLogout();
        setIsLoading(false);
        setUser(undefined);
        window.location.href = '/login';
      }
      setUser(res);
    } catch (error) {
      console.log(error);
    } finally {
      setIsLoading(false);
    }
  };

  const userPlaceOrderRole = user?.placeOrder?.placeOrderRole?.name ?? '';
  const isAdmin = user?.userRoles?.includes(UserRoles.BLAdmin);
  const isFieldAdmin = user?.userRoles?.includes(UserRoles.BLFieldAdmin);
  const isField = user?.userRoles?.includes(UserRoles.BLField);
  const isVendor = user?.userRoles?.includes(UserRoles.BLVendor);
  const isCloser = user?.userRoles?.includes(UserRoles.BLClosing);
  const isBillingAdmin = userPlaceOrderRole === 'Billing Admin';
  const isFinancialAdmin = userPlaceOrderRole === 'Financial Admin';
  const isProjectManager = userPlaceOrderRole.includes('Project Manager');
  const isGlobalAdmin = userPlaceOrderRole === 'Global Admin';
  const isWarrantyAdmin = userPlaceOrderRole === 'Warranty Admin';
  const isPM = user?.userRoles?.some(role =>
    [
      UserRoles.BLProjectManager,
      UserRoles.BLProjectmanager1,
      UserRoles.BLProjectmanager2,
      UserRoles.BLProjectmanager3
    ].includes(role as UserRoles)
  );
  const isPMOrSuperintendent = user?.userRoles?.some(role =>
    [
      UserRoles.BLSuperintendent1,
      UserRoles.BLSuperintendent2,
      UserRoles.BLSuperintendent3,
      UserRoles.BLSuperintendent4,
      UserRoles.BLProjectManager,
      UserRoles.BLProjectmanager1,
      UserRoles.BLProjectmanager2,
      UserRoles.BLProjectmanager3
    ].includes(role as UserRoles)
  );
  const canEdit = isBillingAdmin || isFinancialAdmin || isGlobalAdmin || isWarrantyAdmin;
  const hasWarrantyRole = userPlaceOrderRole.toLowerCase().includes('warranty');
  const isAdditionalUser = user?.isAdditionalUser;
  const canImpersonateInternalUser = user?.userRoles?.includes(UserRoles.BLImpersonationInternal);
  const canImpersonateExternalUser = user?.userRoles?.includes(UserRoles.BLImpersonationExternal);
  const canAccessFileManagement =
    (user?.userRoles?.includes(UserRoles.BLAdmin) && user?.userRoles?.includes(UserRoles.BLClosing)) || isGlobalAdmin;
  const canAccessManageDocumentGroups =
    (user?.userRoles?.includes(UserRoles.BLAdmin) && user?.userRoles?.includes(UserRoles.BLClosing)) || isGlobalAdmin;
  const isSupervisor = user?.userRoles?.some(role =>
    [
      UserRoles.BLSuperintendent2,
      UserRoles.BLSuperintendent3,
      UserRoles.BLSuperintendent4
    ].includes(role as UserRoles)
  );
  const isForeman = user?.userRoles?.some(role =>
    [UserRoles.BLAssistSuper].includes(role as UserRoles)
  );

  return (
    <UserContext.Provider
      value={{
        user,
        isLoading: isLoading || isMSALLoading,
        isEntraLoading: isMSALLoading,
        isProjectManager,
        loadUser,
        setUser,
        isBillingAdmin,
        isFinancialAdmin,
        isGlobalAdmin,
        isWarrantyAdmin,
        isAdmin: isAdmin,
        isFieldAdmin,
        isVendor,
        isCloser,
        isField,
        canEdit,
        hasWarrantyRole,
        isAdditionalUser,
        canImpersonateInternalUser,
        canImpersonateExternalUser,
        canAccessFileManagement,
        canAccessManageDocumentGroups,
        isPM,
        isPMOrSuperintendent,
        isSupervisor,
        isForeman
      }}
    >
      {children}
    </UserContext.Provider>
  );
};
